import { StateCreator } from 'zustand';
import { SemanticSearchStore, PAGE_SIZE, PREVIEW_TAB_KEY, SearchMode, SearchTarget, SortOrder } from './semantic_search.store.types';
import { ISemanticSearchApiService } from '../services/semanticSearchApiService';
import { SearchState, SourceItem } from '../semantic_search.types';
import { Helper } from '../../core/util/helper';

export const createSemanticSearchSlice = (api: ISemanticSearchApiService): StateCreator<
    SemanticSearchStore,
    [["zustand/immer", never]],
    [],
    SemanticSearchStore
> => (set, get) => ({
    searchResult: [],
    checkedSources: null,
    selectedListItem: null,
    activePreviewItem: null,
    textToHighlight: null,
    searchState: SearchState.WaitForInput,
    currentQuery: '',
    currentPage: 1,
    hasMoreResults: false,
    isChatGptEnabled: false,
    searchMode: 'fuzzy',
    searchTarget: 'all',
    sortOrder: 'relevance',
    favorites: [],
    selectedTabKey: PREVIEW_TAB_KEY,

    setChatGptEnabled: (enabled: boolean) => set(state => {
        state.isChatGptEnabled = enabled;
    }),

    startSearch: async (query: string) => {
        if (!Helper.hasValue(query)) {
            get().clearSearch();
            return;
        }

        set(state => {
            state.searchState = SearchState.LoadingSources;
            state.currentQuery = query;
            state.currentPage = 1;
            state.searchResult = [];
            state.checkedSources = null;
            state.selectedListItem = null;
            state.activePreviewItem = null;
            state.hasMoreResults = false;
        });
        
        const { searchMode, searchTarget, sortOrder } = get();
        const isFuzzy = searchMode === 'fuzzy';
        const useWildcards = searchMode === 'wildcard';
        
        const pagedResult = await api.executeSearch(query, 1, PAGE_SIZE, isFuzzy, useWildcards, searchTarget, sortOrder);

        set(state => {
            state.searchResult = pagedResult.Results;
            state.hasMoreResults = pagedResult.HasMore;
            state.searchState = get().isChatGptEnabled ? SearchState.ValidatingSources : SearchState.WaitForInput;
        });

        if (get().isChatGptEnabled) {
            const checkedSources = await api.validateSources(query, get().searchResult);
            set(state => {
                state.checkedSources = checkedSources;
                state.searchState = SearchState.WaitForInput;
            });
        }
    },

    loadMoreResults: async () => {
        const { currentQuery, currentPage, hasMoreResults, searchState, searchMode, searchTarget, sortOrder } = get();
        if (!hasMoreResults || searchState === SearchState.LoadingSources) {
            return;
        }

        set(state => { state.searchState = SearchState.LoadingSources; });

        const newPage = currentPage + 1;
        const isFuzzy = searchMode === 'fuzzy';
        const useWildcards = searchMode === 'wildcard';

        const pagedResult = await api.executeSearch(currentQuery, newPage, PAGE_SIZE, isFuzzy, useWildcards, searchTarget, sortOrder);

        set(state => {
            state.searchResult.push(...pagedResult.Results);
            state.currentPage = newPage;
            state.hasMoreResults = pagedResult.HasMore;
            state.searchState = SearchState.WaitForInput;
        });
    },

    setSelectedListItem: (item: SourceItem) => set(state => {
        state.selectedListItem = item;
        state.activePreviewItem = item;
        state.selectedTabKey = PREVIEW_TAB_KEY;
        
        const sourceIndex = state.searchResult.findIndex(s => s.SourceId === item.SourceId);
        if (sourceIndex !== -1 && state.checkedSources?.UsedSources) {
            const usedSource = state.checkedSources.UsedSources.find(i => parseInt(i.SourceId, 10) === sourceIndex);
            state.textToHighlight = usedSource?.ExactMatchedTexts ?? null;
        } else {
            state.textToHighlight = null;
        }
    }),

    clearSearch: () => set(state => {
        state.searchResult = [];
        state.checkedSources = null;
        state.selectedListItem = null;
        state.activePreviewItem = null;
        state.textToHighlight = null;
        state.searchState = SearchState.WaitForInput;
        state.currentQuery = '';
        state.currentPage = 1;
        state.hasMoreResults = false;
        state.selectedTabKey = PREVIEW_TAB_KEY;
    }),
    
    setSearchMode: (mode: SearchMode) => set(state => {
        state.searchMode = mode;
    }),

    setSearchTarget: (target: SearchTarget) => set(state => {
        state.searchTarget = target;
    }),

    setSortOrder: (order: SortOrder) => set(state => {
        state.sortOrder = order;
    }),

    toggleFavorite: (item: SourceItem) => set(state => {
        const favoriteIndex = state.favorites.findIndex(f => f.SourceId === item.SourceId);
        if (favoriteIndex > -1) {
            const removedFavorite = state.favorites[favoriteIndex];
            state.favorites.splice(favoriteIndex, 1);
            if (state.selectedTabKey === removedFavorite.SourceId) {
                state.selectedTabKey = PREVIEW_TAB_KEY;
                state.activePreviewItem = state.selectedListItem;
            }
        } else {
            state.favorites.push(item);
            state.activePreviewItem = item;
            state.selectedTabKey = item.SourceId;
        }
    }),

    setSelectedTabKey: (key: string) => set(state => {
        state.selectedTabKey = key;
        if (key === PREVIEW_TAB_KEY) {
            state.activePreviewItem = state.selectedListItem;
            // Highlighting should already be set from setSelectedListItem, so no change here.
        } else {
            const favorite = state.favorites.find(f => f.SourceId === key);
            if (favorite) {
                state.activePreviewItem = favorite;
                state.textToHighlight = null; // Highlighting is not applicable for direct favorite selection
            }
        }
    }),
});